import { useEffect, useRef } from "react";

// Cap on WebOTP requests per mount (reset on resend), so a browser that keeps rejecting
// immediately can't loop forever.
const MAX_WEBOTP_REQUESTS = 5;

// Auto-reads the OTP from the SMS via the WebOTP API (Chrome on Android) and hands the
// 4-digit code to onCode.
//
// Chrome only delivers a code to a request that is still pending when the user taps
// "Allow" - once a request has settled it unsubscribes, and a late "Allow" is dropped.
// So whenever a request ends without a code (other than our own abort on unmount), a new
// one is started right away, keeping a listener alive for as long as the OTP screen is up.
// Chrome itself ends each request after 4 minutes (blink::kWebOTPRequestTimeout).
//
// Returns a function to call when the OTP is resent, so the new SMS is listened for even
// if the earlier requests were used up.
const useWebOtpAutofill = (onCode, debugLabel = "") => {
  // Always call the latest onCode, even though the effect only runs once at mount.
  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;
  const restartRef = useRef(() => {});

  useEffect(() => {
    const log = (...args) => console.log(`DEBUG WebOTP ${debugLabel}:`, ...args);
    if (!("OTPCredential" in window) || !navigator.credentials?.get) {
      log("not supported in this browser, skipping");
      return;
    }

    let unmounted = false;
    let pending = false;
    let codeReceived = false;
    let requestCount = 0;
    let abortController;

    const request = () => {
      if (unmounted || pending || codeReceived) return;
      if (requestCount >= MAX_WEBOTP_REQUESTS) {
        log("request limit reached, not listening any more");
        return;
      }
      requestCount += 1;
      pending = true;
      const controller = new AbortController();
      abortController = controller;
      log(`request #${requestCount}: calling navigator.credentials.get, waiting for SMS`);
      navigator.credentials.get({
        otp: { transport: ["sms"] },
        signal: controller.signal,
      }).then((otpCredential) => {
        pending = false;
        log("credentials.get resolved", { rawCode: otpCredential?.code });
        if (unmounted) return;
        const sanitizedValue = otpCredential?.code?.replace(/[^\d]/g, "").slice(0, 4);
        if (sanitizedValue?.length === 4) {
          codeReceived = true;
          onCodeRef.current(sanitizedValue);
          log("otp filled", sanitizedValue);
        } else {
          log("code did not have 4 digits, listening again", sanitizedValue);
          request();
        }
      }).catch((err) => {
        pending = false;
        log("credentials.get rejected", err?.name, err?.message);
        // Our own abort (unmount) - stop. Anything else (timeout, cancel) - listen again.
        if (controller.signal.aborted || unmounted) return;
        request();
      });
    };

    restartRef.current = () => {
      log("otp resent, listening for the new SMS");
      codeReceived = false;
      requestCount = 0;
      request();
    };

    request();
    return () => {
      log("unmount, aborting pending request");
      unmounted = true;
      restartRef.current = () => {};
      abortController?.abort();
    };
  }, []);

  return () => restartRef.current();
};

export default useWebOtpAutofill;
